import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, ChevronDown } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { SECTORS } from "@/lib/sectors";

const navLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors hover:text-emerald-600 ${isActive ? "text-emerald-600" : "text-slate-700"}`;

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [claimsOpen, setClaimsOpen] = useState(false);
    const navigate = useNavigate();

    return (
        <header className="sticky top-0 z-40 w-full border-b border-slate-200/80 bg-white/80 backdrop-blur-xl">
            <div className="container-x flex items-center justify-between h-16 lg:h-20">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2.5 group">
                    <div className="relative w-9 h-9 rounded-xl bg-navy-900 flex items-center justify-center overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 via-transparent to-transparent" />
                        <ShieldCheck className="w-5 h-5 text-emerald-400 relative" />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-display font-bold text-navy-900 text-lg tracking-tight">ClaimHub <span className="text-emerald-600">UK</span></span>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">No Win · No Fee</span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-7">
                    <NavLink to="/" data-testid={NAV.home} className={navLinkClass} end>Home</NavLink>

                    <div
                        className="relative"
                        onMouseEnter={() => setClaimsOpen(true)}
                        onMouseLeave={() => setClaimsOpen(false)}
                    >
                        <button
                            data-testid={NAV.claims}
                            className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-emerald-600 transition-colors"
                        >
                            Claim types <ChevronDown className="w-4 h-4" />
                        </button>
                        {claimsOpen && (
                            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[560px]">
                                <div className="bg-white rounded-2xl border border-slate-200 shadow-glow-emerald p-2 grid grid-cols-2 gap-1">
                                    {SECTORS.map((s) => (
                                        <Link
                                            key={s.slug}
                                            to={`/sectors/${s.slug}`}
                                            className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-emerald-50 transition-colors"
                                        >
                                            <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
                                            <div>
                                                <div className="text-sm font-semibold text-navy-900">{s.name}</div>
                                                <div className="text-xs text-slate-500">{s.short}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <NavLink to="/how-it-works" data-testid={NAV.howItWorks} className={navLinkClass}>How it works</NavLink>
                    <NavLink to="/about" data-testid={NAV.about} className={navLinkClass}>About</NavLink>
                    <NavLink to="/faq" data-testid={NAV.faq} className={navLinkClass}>FAQ</NavLink>
                    <NavLink to="/contact" data-testid={NAV.contact} className={navLinkClass}>Contact</NavLink>
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <button
                        data-testid={NAV.startClaimCta}
                        onClick={() => navigate("/claim")}
                        className="btn-primary text-sm py-2.5 px-5"
                    >
                        Start your claim
                    </button>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    data-testid={NAV.mobileToggle}
                    className="lg:hidden w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-navy-900"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-slate-200 bg-white">
                    <div className="container-x py-4 flex flex-col gap-1">
                        <NavLink to="/" onClick={() => setOpen(false)} className="py-2.5 text-base font-medium text-slate-800">Home</NavLink>
                        <details className="group">
                            <summary className="py-2.5 text-base font-medium text-slate-800 cursor-pointer list-none flex items-center justify-between">
                                Claim types <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                            </summary>
                            <div className="pl-3 py-1 flex flex-col">
                                {SECTORS.map((s) => (
                                    <Link key={s.slug} to={`/sectors/${s.slug}`} onClick={() => setOpen(false)} className="py-2 text-sm text-slate-600 hover:text-emerald-600">{s.name}</Link>
                                ))}
                            </div>
                        </details>
                        <NavLink to="/how-it-works" onClick={() => setOpen(false)} className="py-2.5 text-base font-medium text-slate-800">How it works</NavLink>
                        <NavLink to="/about" onClick={() => setOpen(false)} className="py-2.5 text-base font-medium text-slate-800">About</NavLink>
                        <NavLink to="/faq" onClick={() => setOpen(false)} className="py-2.5 text-base font-medium text-slate-800">FAQ</NavLink>
                        <NavLink to="/contact" onClick={() => setOpen(false)} className="py-2.5 text-base font-medium text-slate-800">Contact</NavLink>
                        <button
                            onClick={() => { setOpen(false); navigate("/claim"); }}
                            className="btn-primary mt-3 w-full"
                        >
                            Start your claim
                        </button>
                    </div>
                </div>
            )}
        </header>
    );
}
